import axios from 'axios';
import { API_BASE_URL, attachAuthInterceptors } from './session';
import { connectSSE, type SSEEventHandler } from './v2-chat-api';
import type { ChatMessage } from '@/types/chat';
import type {
  BusinessIntegration,
  DataSource,
  ConnectionSetupData,
  ConnectionTestResult,
} from '../types';

const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
  },
});

attachAuthInterceptors(api);

// Business Integrations API
export const integrationsApi = {
  // Get available integrations
  getAvailableIntegrations: async (): Promise<BusinessIntegration[]> => {
    const response = await api.get('/api/integrations/available');
    return response.data.integrations;
  },

  // Get connected integrations (data sources)
  getConnectedIntegrations: async (): Promise<DataSource[]> => {
    const response = await api.get('/api/integrations/connected');
    return response.data.dataSources;
  },

  // Test connection without saving
  testConnection: async (
    integrationType: string,
    credentials: Record<string, unknown>
  ): Promise<ConnectionTestResult> => {
    const response = await api.post(`/api/integrations/${integrationType}/test`, {
      credentials,
    });
    return response.data;
  },

  // Connect new integration
  connectIntegration: async (
    integrationType: string,
    setupData: ConnectionSetupData
  ): Promise<{ dataSource: DataSource; syncResult?: unknown }> => {
    const response = await api.post(`/api/integrations/${integrationType}/connect`, setupData);
    return response.data;
  },

  // Disconnect integration
  disconnectIntegration: async (dataSourceId: string): Promise<void> => {
    await api.delete(`/api/integrations/data-sources/${dataSourceId}`);
  },

  // Sync integration data
  syncIntegration: async (dataSourceId: string): Promise<{
    synced: number;
    errors: string[];
    lastSync: string;
  }> => {
    const response = await api.post(`/api/integrations/data-sources/${dataSourceId}/sync`);
    return response.data;
  },

  // Update integration settings
  updateIntegration: async (
    dataSourceId: string,
    settings: { name?: string; syncEnabled?: boolean }
  ): Promise<DataSource> => {
    const response = await api.put(`/api/integrations/data-sources/${dataSourceId}`, settings);
    return response.data.dataSource;
  },

  // Get sync history
  getSyncHistory: async (dataSourceId: string): Promise<Array<{
    id: string;
    timestamp: string;
    status: 'success' | 'error' | 'partial';
    recordsProcessed: number;
    errors?: string[];
  }>> => {
    const response = await api.get(`/api/integrations/data-sources/${dataSourceId}/sync-history`);
    return response.data.history;
  },

  // Webhook-specific endpoints
  createWebhookDataSource: async (name: string, organizationId: string): Promise<{
    id: string;
    webhookId: string;
    webhookUrl: string;
  }> => {
    const response = await api.post('/api/integrations/webhook-custom/create', {
      name,
      organizationId
    });
    return response.data.data;
  },

  getWebhookSample: async (dataSourceId: string): Promise<{
    hasSample: boolean;
    samplePayload?: Record<string, unknown>;
    sampleCapturedAt?: string;
  }> => {
    const response = await api.get(`/api/integrations/data-sources/${dataSourceId}/sample`);
    return response.data.data;
  },

  getWebhookMappingConfig: async (dataSourceId: string): Promise<{
    id: string;
    name: string;
    sourceType: string;
    webhookUrl: string;
    setupStatus: string;
    hasMapping: boolean;
    mapping: WebhookMappingResponse | null;
    samplePayload: Record<string, unknown> | null;
    sampleCapturedAt: string | null;
  }> => {
    const response = await api.get(`/api/integrations/data-sources/${dataSourceId}/mapping`);
    return response.data.data;
  },

  saveWebhookMapping: async (
    dataSourceId: string,
    mapping: SaveWebhookMappingRequest,
  ): Promise<{
    success: boolean;
    message: string;
    preview?: {
      transaction: Record<string, unknown>;
      details: Record<string, unknown>[];
      hasDetails: boolean;
      itemCount: number;
    };
    warnings?: string[];
  }> => {
    const response = await api.post(`/api/integrations/data-sources/${dataSourceId}/mapping`, {
      mapping,
    });
    return response.data;
  },

  detectWebhookArrays: async (dataSourceId: string): Promise<{
    success: boolean;
    data: {
      arrays: Array<{
        path: string;
        itemCount: number;
        sampleItemKeys: string[];
        sampleItem: unknown;
      }>;
    };
  }> => {
    const response = await api.get(`/api/integrations/data-sources/${dataSourceId}/detect-arrays`);
    return response.data;
  },

  testWebhookMapping: async (
    dataSourceId: string,
    mapping: SaveWebhookMappingRequest,
  ): Promise<{
    success: boolean;
    data: {
      isValid: boolean;
      errors: string[];
      warnings?: string[];
      preview: {
        transaction: Record<string, unknown>;
        details: Record<string, unknown>[];
        hasDetails: boolean;
        itemCount: number;
      } | null;
      transformError?: string;
    };
  }> => {
    const response = await api.post(`/api/integrations/data-sources/${dataSourceId}/test-mapping`, {
      mapping,
    });
    return response.data;
  },

  // Get data source configuration for editing (Clover, QuickBooks, etc.)
  getDataSourceConfig: async (dataSourceId: string): Promise<{
    id: string;
    name: string;
    sourceType: string;
    healthStatus: string;
    syncEnabled: boolean;
    syncFrequency: string;
    editableSettings: Record<string, string | number | boolean | null>;
    configSchema: Record<string, {
      type: 'text' | 'select' | 'boolean' | 'number' | 'date';
      label: string;
      description?: string;
      options?: Array<{ value: string; label: string }>;
      readOnly?: boolean;
    }>;
    lastSync?: string;
    createdAt: string;
  }> => {
    const response = await api.get(`/api/integrations/data-sources/${dataSourceId}/config`);
    return response.data.data;
  },

  // Update data source configuration
  updateDataSourceConfig: async (
    dataSourceId: string,
    settings: Record<string, string | number | boolean | null | undefined>,
  ): Promise<{
    success: boolean;
    message: string;
  }> => {
    const response = await api.put(`/api/integrations/data-sources/${dataSourceId}/config`, {
      settings
    });
    return response.data;
  },

  // Update account settings (business_timezone, etc.)
  updateAccountSettings: async (accountId: string, settings: { business_timezone?: string | null }): Promise<{
    success: boolean;
    message: string;
    data: { business_timezone: string | null };
  }> => {
    const response = await api.put(`/api/integrations/accounts/${accountId}/settings`, settings);
    return response.data;
  },

  // Get column sample values for a data source account
  getAccountColumnSamples: async (accountId: string): Promise<{
    success: boolean;
    data: {
      accountId: string;
      samples: {
        transactions: Record<string, string[]>;
        transaction_details: Record<string, string[]>;
        transaction_detail_options?: Record<string, string[]>;
      };
    };
  }> => {
    const response = await api.get(`/api/integrations/accounts/${accountId}/column-samples`);
    return response.data;
  },

  // ============================================================================
  // Field Mapping & Override API
  // ============================================================================

  getAccountFieldMappings: async (accountId: string): Promise<{
    success: boolean;
    data: {
      accountId: string;
      fields: EffectiveField[];
    };
  }> => {
    const response = await api.get(`/api/integrations/accounts/${accountId}/field-mappings`);
    return response.data;
  },

  getAccountFieldOverrides: async (accountId: string): Promise<{
    success: boolean;
    data: {
      accountId: string;
      overrides: FieldAccountOverride[];
    };
  }> => {
    const response = await api.get(`/api/integrations/accounts/${accountId}/field-overrides`);
    return response.data;
  },

  updateAccountFieldOverrides: async (
    accountId: string,
    overrides: FieldOverrideInput[],
  ): Promise<{
    success: boolean;
    message: string;
    data: { overrides: FieldAccountOverride[] };
  }> => {
    const response = await api.put(`/api/integrations/accounts/${accountId}/field-overrides`, {
      overrides,
    });
    return response.data;
  },

  deleteAccountFieldOverride: async (
    accountId: string,
    mappingLevel: string,
    dbColumn: string,
  ): Promise<{ success: boolean; message: string }> => {
    const response = await api.delete(
      `/api/integrations/accounts/${accountId}/field-overrides/${mappingLevel}/${dbColumn}`,
    );
    return response.data;
  },

  // ============================================================================
  // Inbound file feeds — onboarding + allow-list management
  // ============================================================================

  getInboundFileSource: async (dataSourceId: string): Promise<InboundFileSource> => {
    const response = await api.get(`/api/integrations/inbound-file/${dataSourceId}`);
    return response.data.data;
  },

  updateInboundFilePurpose: async (
    dataSourceId: string,
    summaryPurpose: string,
  ): Promise<{ id: string; summaryPurpose: string }> => {
    const response = await api.patch(
      `/api/integrations/inbound-file/${dataSourceId}/purpose`,
      { summaryPurpose },
    );
    return response.data.data;
  },

  addInboundFileSender: async (
    dataSourceId: string,
    email: string,
  ): Promise<{ allowedSenders: string[] }> => {
    const response = await api.post(
      `/api/integrations/inbound-file/${dataSourceId}/allowed-senders`,
      { email },
    );
    return response.data.data;
  },

  removeInboundFileSender: async (
    dataSourceId: string,
    email: string,
  ): Promise<{ allowedSenders: string[] }> => {
    const response = await api.delete(
      `/api/integrations/inbound-file/${dataSourceId}/allowed-senders`,
      { data: { email } },
    );
    return response.data.data;
  },

  // Runs (one inbound_emails row is one import run)
  listInboundEmails: async (
    dataSourceId: string,
    opts?: { status?: InboundEmailStatus; limit?: number; offset?: number },
  ): Promise<{
    emails: InboundEmailListItem[];
    total: number;
    limit: number;
    offset: number;
  }> => {
    const params: Record<string, string | number> = {};
    if (opts?.status) params.status = opts.status;
    if (opts?.limit != null) params.limit = opts.limit;
    if (opts?.offset != null) params.offset = opts.offset;
    const response = await api.get(
      `/api/integrations/inbound-file/${dataSourceId}/emails`,
      { params },
    );
    return response.data.data;
  },

  getInboundEmailDetail: async (
    dataSourceId: string,
    emailId: string,
  ): Promise<InboundEmailDetail> => {
    const response = await api.get(
      `/api/integrations/inbound-file/${dataSourceId}/emails/${emailId}`,
    );
    return response.data.data;
  },

  reprocessInboundEmail: async (
    dataSourceId: string,
    emailId: string,
  ): Promise<{ id: string; status: string }> => {
    const response = await api.post(
      `/api/integrations/inbound-file/${dataSourceId}/emails/${emailId}/reprocess`,
    );
    return response.data.data;
  },

  /**
   * A file run's excluded rows as they appear in the file. Served by re-reading
   * the file per request — nothing extra is stored — so an unavailable file comes
   * back as `available: false` with a reason rather than an error.
   */
  listRunExclusions: async (
    dataSourceId: string,
    emailId: string,
  ): Promise<RunExclusionsPayload> => {
    const response = await api.get(
      `/api/integrations/inbound-file/${dataSourceId}/emails/${emailId}/exclusions`,
    );
    return response.data.data;
  },

  // ============================================================================
  // Inbound File (dynamic file ingestion — learned-map review)
  // Management endpoints (get/purpose/senders/emails) are shared with inbound
  // email: the same functions above work for file sources too.
  // ============================================================================

  createInboundFileSource: async (
    name: string,
    summaryPurpose: string,
    organizationId?: string | null,
  ): Promise<InboundFileSource> => {
    const response = await api.post('/api/integrations/inbound-file/create', {
      name,
      summaryPurpose,
      ...(organizationId ? { organizationId } : {}),
    });
    return response.data.data;
  },

  getInboundFileDraft: async (dataSourceId: string): Promise<InboundFileDraftBundle> => {
    const response = await api.get(`/api/integrations/inbound-file/${dataSourceId}/draft`);
    return response.data.data;
  },

  approveInboundFileDraft: async (
    dataSourceId: string,
  ): Promise<{ approved: InboundFileVersion; requeuedFiles: number }> => {
    const response = await api.post(`/api/integrations/inbound-file/${dataSourceId}/draft/approve`);
    return response.data.data;
  },

  /** The caller's persistent mapping-discussion thread and its history. */
  getInboundFileDiscuss: async (dataSourceId: string): Promise<InboundFileDiscussState> => {
    const response = await api.get(`/api/integrations/inbound-file/${dataSourceId}/discuss`);
    return response.data.data;
  },

  /** Streaming discuss turn — the agent's tool calls edit the draft in place. */
  discussInboundFile: (
    dataSourceId: string,
    message: string,
    onEvent: SSEEventHandler,
    signal?: AbortSignal,
  ): Promise<void> =>
    connectSSE(
      `/api/integrations/inbound-file/${dataSourceId}/discuss`,
      { message },
      onEvent,
      signal,
    ),

  /**
   * Direct remap (no LLM): the server re-validates and re-runs the dry run.
   * Silenced because the inspector renders 422 `details[]` inline.
   */
  patchInboundFileDraft: async (
    dataSourceId: string,
    spec: InboundFileSpec,
  ): Promise<{ draft: InboundFileVersion }> => {
    const response = await api.patch(
      `/api/integrations/inbound-file/${dataSourceId}/draft`,
      { spec },
      { silenceToast: true },
    );
    return response.data.data;
  },
};

// ============================================================================
// Types
// ============================================================================

export interface InboundFileSource {
  id: string;
  name?: string;
  inboundAddress: string;
  summaryPurpose: string | null;
  allowedSenders: string[];
  setupStatus: 'active' | 'paused';
}

export type InboundEmailStatus =
  | 'received'
  | 'unmatched'
  | 'rejected_sender'
  | 'processing'
  | 'completed'
  | 'discarded'
  | 'needs_review'
  | 'failed'
  | 'awaiting_map'
  | 'quarantined';

// ── Inbound File (learned-map review) ───────────────────────────────────────

export interface InboundFileBinding {
  slot: string;
  source: string[];
  friendlySlug: string;
  friendlyLabel: string;
  description: string;
  dataType: FieldDataType;
  unit?: string;
  masterMetric?: boolean;
  transform?: { op: string; [key: string]: unknown };
}

export interface InboundFileSkippedColumn {
  column: string;
  reason: string;
  /** The skip was a judgment call the reviewer might reasonably reverse. */
  worthALook?: boolean;
}

export interface InboundFileSpec {
  classification: 'fact' | 'dimension' | 'reject';
  sourceLabel: string;
  timezone: string;
  currency?: string;
  fingerprint: { columns: string[]; delimiter: string; headerRow: number };
  bindings: InboundFileBinding[];
  naturalKey: string[];
  rowRules?: Array<{ slot: string; op: string; value?: unknown; reason?: string }>;
  wikiDraft?: string;
  proposalExplanation?: string;
  skippedColumns?: InboundFileSkippedColumn[];
}

export interface InboundFileColumnProfile {
  column: string;
  bound: boolean;
  nullRate: number;
  sampleValues?: string[];
  distinctValues?: string[];
  distinctOverflow?: boolean;
  min?: number;
  max?: number;
  sum?: number;
}

export interface InboundFileCoverage {
  minDate: string;
  maxDate: string;
  rowsPerDay: Record<string, number>;
  rowsPerDayOverflow?: boolean;
}

/**
 * Profiles stored before the review grid shipped lack coverage/preview*; the
 * server heals them on the next GET, so treat them as optional here.
 */
export interface InboundFileProfile {
  totalRows: number;
  parsedRows: number;
  rejectedRows: number;
  failures: Array<{ row: number; reason: string }>;
  naturalKeyCollisions: number;
  columns: InboundFileColumnProfile[];
  sampleRows: Array<Record<string, string | number | boolean | null>>;
  coverage?: InboundFileCoverage | null;
  previewHeaders?: string[];
  previewRows?: string[][];
}

export interface InboundFileVersion {
  id: string;
  version: string;
  status: 'draft' | 'approved' | 'superseded';
  spec: InboundFileSpec;
  profile: InboundFileProfile | null;
  approvedBy: string | null;
  approvedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface InboundFileDraftBundle {
  draft: InboundFileVersion | null;
  approved: InboundFileVersion | null;
  counts: { awaitingMap: number; quarantined: number; completed: number };
}

/** The persistent mapping-discussion thread for a feed (one per feed + user). */
export interface InboundFileDiscussState {
  threadId: string | null;
  messages: ChatMessage[];
}

/** One row a run did not write. Rule rejects carry the index of the rule that tripped. */
export interface RunExclusion {
  row: number;
  kind: 'rule' | 'parse';
  reason: string;
  ruleIndex?: number;
}

/**
 * The import-run record of a file, from `inbound_emails.import_stats`. Runs
 * recorded before the Runs surface shipped carry only the original six counts,
 * so everything added since is optional here even though the server type has it.
 */
export interface ImportStats {
  totalRows: number;
  writtenRows: number;
  newRows?: number;
  updatedRows?: number;
  rejectedRows: number;
  failedRows: number;
  duplicateKeys?: number;
  coverage?: { minDate: string; maxDate: string } | null;
  exclusions?: RunExclusion[];
  exclusionOverflow?: boolean;
  durationMs: number;
  mappingVersionId: string;
}

export interface RunExclusionColumn {
  /** Raw file header, in file order. */
  header: string;
  /** Bound slot, or null when the map skips this column. */
  slot: string | null;
  label: string | null;
  masterMetric: boolean;
}

/** An excluded row with its raw cells — served on demand, never stored. */
export interface RunExclusionRow {
  row: number;
  kind: 'rule' | 'parse';
  reason: string;
  cells: string[];
  /** Index into `columns` of the cell that tripped it, when derivable. */
  culpritColumn?: number;
}

export interface RunExclusionsPayload {
  available: boolean;
  /** Set when `available` is false — render it verbatim; the stored counts still hold. */
  unavailableReason?: string;
  columns: RunExclusionColumn[];
  rows: RunExclusionRow[];
  truncated: boolean;
}

export interface InboundEmailListItem {
  id: string;
  sender: string;
  subject: string | null;
  status: InboundEmailStatus;
  rejectReason: string | null;
  attachmentCount: number;
  /** First attachment's filename — the run's identity on a file feed. */
  fileName: string | null;
  transactionCount: number;
  errorMessage: string | null;
  receivedAt: string;
  processedAt: string | null;
  /** File feeds only; null for email sources and for runs that never executed. */
  importStats: ImportStats | null;
}

export interface InboundEmailAttachmentMeta {
  name: string;
  contentType?: string;
  size?: number;
}

export interface InboundEmailDetail extends InboundEmailListItem {
  recipient: string;
  attachments: InboundEmailAttachmentMeta[];
  createdTransactionIds: string[];
  llmSessionId: string | null;
}

export type MappingLevel = 'transactions' | 'transaction_details' | 'transaction_detail_options';

export type FieldDataType =
  | 'currency' | 'count' | 'name' | 'category' | 'reference'
  | 'boolean' | 'date' | 'percentage' | 'text' | 'identifier';

export interface EffectiveField {
  mappingLevel: MappingLevel;
  dbColumn: string;
  friendlySlug: string;
  displayLabel: string;
  displayDescription: string;
  dataType: FieldDataType;
  unit?: string;
  isActive: boolean;
  isMasterMetric: boolean;
  conversionValue?: number;
}

export interface FieldAccountOverride {
  id: string;
  data_source_account_id: string;
  mapping_level: MappingLevel;
  db_column: string;
  is_active: boolean;
  is_master_metric: boolean;
  conversion_value?: number | null;
  custom_label?: string | null;
  custom_description?: string | null;
  notes?: string | null;
  created_at: string;
  updated_at: string;
}

export interface FieldOverrideInput {
  mapping_level: MappingLevel;
  db_column: string;
  is_active?: boolean;
  is_master_metric?: boolean;
  conversion_value?: number | null;
  custom_label?: string | null;
  custom_description?: string | null;
  notes?: string | null;
}

// ============================================================================
// Webhook Field Mapping Types (Phase 6)
// ============================================================================

export type ComputedOperationType =
  | 'dateDiffSeconds' | 'dateDiffMinutes' | 'dateDiffHours' | 'dateDiffDays'
  | 'add' | 'subtract' | 'multiply' | 'divide' | 'percentage'
  | 'arraySum' | 'arrayCount' | 'arrayAvg'
  | 'concat' | 'constant';

export interface TransformConfig {
  type: 'unix_seconds' | 'unix_ms' | 'iso8601' | 'divide' | 'multiply'
    | 'value_mapping' | 'uppercase' | 'lowercase' | 'parse_number' | 'default_value';
  divisor?: number;
  multiplier?: number;
  mapping?: Record<string, string>;
  defaultValue?: string | number | boolean | null;
}

export interface ComputedFieldConfig {
  type: 'computed';
  operation: ComputedOperationType;
  operand1: string;
  operand2?: string;
  arrayField?: string;
  constantValue?: number | string;
  separator?: string;
  roundTo?: number;
}

export interface WebhookColumnMappingInput {
  dbColumn: string;
  sourceApiField: string;
  friendlySlug: string;
  friendlyLabel: string;
  description: string;
  dataType: FieldDataType;
  standardizedField?: string;
  transformConfig?: TransformConfig | ComputedFieldConfig;
  transformNote?: string;
  unit?: string;
  defaultMasterMetric?: boolean;
}

export interface SaveWebhookMappingRequest {
  transactionMappings: WebhookColumnMappingInput[];
  detailMappings?: WebhookColumnMappingInput[];
  detailsArrayPath?: string;
}

export interface WebhookMappingResponse {
  mappingVersionId: string | null;
  transactionMappings: WebhookColumnMappingInput[];
  detailMappings: WebhookColumnMappingInput[];
  detailsArrayPath?: string;
}